import { useContainerBlockContext } from '@lighthouse/block'
import type { FilterFormType, VariableFieldADTValue } from '@lighthouse/core'
import {
    AppUserStatus,
    type DataSourceAbstract,
    type FilterCommonCondition,
    type GroupConfigure,
    type GroupRecordCount,
    type GroupTab,
    VariableType
} from '@lighthouse/core'
import {
    ALL_GROUP,
    EMPTY_COLUMN_GROUP,
    getGroupAllVisibleOptions,
    getGroupVisibleWithLabelOptions,
    getWithScopeId,
    mergeGroupVisibleOptions,
    useApplicationContext,
    useAtomAction,
    useHandleAbortPrevious
} from '@lighthouse/shared'
import { useDeepCompareMemo } from '@react-hookz/web'
import { useAtomValue } from 'jotai'
import { find } from 'rambda'
import { useCallback, useMemo, useRef, useState } from 'react'

import { groupCacheAtomFamily } from '@/atoms/storage/state'
import * as srv from '@/services'

type handleGetGroupRecordCountPayload = {
    quickFilters?: FilterCommonCondition[]
    linkFilter?: FilterFormType
    filter?: FilterFormType
    search?: string
}

interface useGroupPayload {
    appId: string
    envId: string
    blockId: string
    dataSource: DataSourceAbstract | undefined
    canGroup: boolean | undefined
    groupConfigure: GroupConfigure | undefined
}

export const useGroup = (payload: useGroupPayload) => {
    const { appId, envId, blockId, dataSource, canGroup, groupConfigure } = payload
    const { groupByFieldId = '', groupConfig, canHiddenTabValue } = groupConfigure || {}
    const { mutation: getGroupRecordCountMutation } = useHandleAbortPrevious(srv.getGroupRecordCount)
    const { scope } = useContainerBlockContext()
    const { personOptions } = useApplicationContext()
    const withScopeId = getWithScopeId(blockId, scope)
    const groupCache = useAtomValue(groupCacheAtomFamily({ appId, envId, id: withScopeId }))
    const { run: setGroupCache } = useAtomAction(groupCacheAtomFamily({ appId, envId, id: withScopeId }))
    const [tabValue, setTabValue] = useState<string | undefined>(undefined)
    // const [tabCountList, setTabCountList] = useImmer<GroupRecordCount[]>([])
    const [tabList, setTabList] = useState<GroupTab[]>([])

    const groupByField = useMemo(() => {
        if (!dataSource) {
            return
        }
        const { schema } = dataSource
        const field = schema[groupByFieldId]
        if (!field) {
            return
        }
        return field
    }, [dataSource, groupByFieldId])

    const initGroupConfigData = useMemo(() => {
        if (groupConfig) {
            if (!groupByField) {
                return []
            }
            return mergeGroupVisibleOptions(
                getGroupVisibleWithLabelOptions(groupByField, groupConfig, {
                    personOptions
                }),
                getGroupAllVisibleOptions(groupByField, { personOptions }, true)
            )
        }
        return []
    }, [groupByField, groupConfig, personOptions])

    const initGroupData = useMemo(
        () => ({
            groupByFieldId: groupByField?.id || '',
            canHiddenTabValue,
            groupConfig: initGroupConfigData
        }),
        [canHiddenTabValue, groupByField, initGroupConfigData]
    )

    const initGroup: GroupConfigure = useDeepCompareMemo(() => {
        if (canGroup) {
            return groupCache || initGroupData
        }
        return initGroupData
    }, [canGroup, groupCache, initGroupData])

    const groupByFieldIdRef = useRef(initGroup.groupByFieldId)

    const initTabList = useMemo(() => {
        if (!initGroup?.groupConfig) {
            return []
        }
        return initGroup.groupConfig
            ?.filter(v => v.visible)
            .map(item => ({
                value: item.value,
                label: item.label,
                count: undefined
            }))
    }, [initGroup.groupConfig])

    const tab = useMemo(() => {
        if (groupByFieldIdRef.current === initGroup.groupByFieldId) {
            return tabValue
        }
        groupByFieldIdRef.current = initGroup.groupByFieldId
    }, [initGroup.groupByFieldId, tabValue])

    const handleTabChange = useCallback((val: string) => {
        setTabValue(val)
    }, [])

    const handleGroupChange = useCallback(
        (data: GroupConfigure) => {
            setGroupCache(data)
        },
        [setGroupCache]
    )

    const getFilterByTab = useCallback<(tab: string | undefined) => FilterCommonCondition[]>(
        tab => {
            if (!tab || tab === ALL_GROUP || !initGroup) {
                return []
            }
            if (tab === EMPTY_COLUMN_GROUP) {
                return [
                    {
                        idVariable: { type: VariableType.FIELD_ID, fieldIdVariable: { fieldId: initGroup.groupByFieldId } },
                        operator: 'isEmpty',
                        paramList: [{ type: VariableType.VALUE }]
                    }
                ]
            }
            const field = dataSource?.schema?.[initGroup.groupByFieldId]
            if (!field) {
                return []
            }
            return [
                {
                    idVariable: { type: VariableType.FIELD_ID, fieldIdVariable: { fieldId: initGroup.groupByFieldId } },
                    operator: 'contains',
                    paramList: [{ type: VariableType.VALUE, valueVariable: { type: field.type, value: [tab] } as VariableFieldADTValue }]
                }
            ]
        },
        [dataSource?.schema, initGroup]
    )

    const handleSetTabList = useCallback(
        (tabCountList: GroupRecordCount[] | undefined) => {
            if (!tabCountList || tabCountList.length === 0) {
                return
            }
            const list = initTabList.reduce<GroupTab[]>((list, item) => {
                const data = find(v => v.value === item.value, tabCountList)
                const person = find(v => v.id === item.value, personOptions)
                if (person?.status === AppUserStatus.DEPART) {
                    return list
                }

                if (initGroup?.canHiddenTabValue && data?.count === 0) {
                    return list
                }
                list.push({
                    label: item.label,
                    value: item.value,
                    count: data?.count
                })
                return list
            }, [])
            const firstTab = list?.[0]?.value
            setTabList(list)
            setTabValue(firstTab || 'ALL_GROUP')
        },
        [initGroup?.canHiddenTabValue, initTabList, personOptions]
    )

    const handleGetGroupRecordCount = useCallback(
        async (payload: handleGetGroupRecordCountPayload) => {
            if (initGroup?.groupByFieldId) {
                const data = await getGroupRecordCountMutation({
                    appId,
                    viewId: blockId,
                    isSyncComponent: !!scope,
                    fieldId: initGroup?.groupByFieldId || '',
                    ...payload
                })
                handleSetTabList(data)
                return
            }
            setTabList([])
            setTabValue(undefined)
        },
        [appId, blockId, getGroupRecordCountMutation, handleSetTabList, initGroup?.groupByFieldId, scope]
    )

    return useMemo(
        () => ({
            initTabList,
            initGroup,
            tabValue: tab,
            tabList,
            setGroupCache,
            handleTabChange,
            getFilterByTab,
            handleGroupChange,
            handleGetGroupRecordCount
        }),
        [getFilterByTab, handleGetGroupRecordCount, handleGroupChange, handleTabChange, initGroup, initTabList, setGroupCache, tab, tabList]
    )
}
